/**
 * Centralized async reliability: timeouts, retries, normalized user-safe errors, dev-only logs.
 * Use for Supabase calls, PDF generation, and any action that must never strand loading UI.
 */

import { devClickDebug } from '../devClickDebug';
import { devLog, devError } from './logger';
import { readValidSupabaseAccessTokenFromLocalStorage } from '../supabaseProfilesDiag';

const IS_DEV = process.env.NODE_ENV === 'development';

/** @param {string} phase @param {Record<string, unknown>} payload */
export function devReliabilityLog(phase, payload = {}) {
  if (!IS_DEV) return;
  devLog(`[Devello async] ${phase}`, payload);
}

/**
 * @param {unknown} err
 * @returns {'timeout'|'network'|'auth'|'validation'|'server'|'unknown'}
 */
export function classifyError(err) {
  if (!err) return 'unknown';
  if (typeof err === 'object' && err !== null) {
    const st = /** @type {{ status?: number }} */ (err).status;
    if (typeof st === 'number') {
      if (st === 401 || st === 403) return 'auth';
      if (st >= 500) return 'server';
      if (st === 408 || st === 504) return 'timeout';
    }
    if (/** @type {{ code?: string }} */ (err).code === 'TIMEOUT') return 'timeout';
    if (/** @type {{ isTimeout?: boolean }} */ (err).isTimeout) return 'timeout';
    const name = /** @type {{ name?: string }} */ (err).name;
    if (name === 'AbortError') return 'network';
  }
  const msg = String(
    /** @type {{ message?: string }} */ (err).message || err
  ).toLowerCase();
  if (msg.includes('timeout') || msg.includes('timed out')) return 'timeout';
  if (
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('load failed') ||
    msg.includes('network request failed')
  ) {
    return 'network';
  }
  if (
    msg.includes('jwt') ||
    msg.includes('unauthorized') ||
    msg.includes('invalid refresh token') ||
    (msg.includes('session') && (msg.includes('expired') || msg.includes('invalid')))
  ) {
    return 'auth';
  }
  if (msg.includes('validation') || msg.includes('invalid input')) return 'validation';
  if (msg.includes('gateway') || msg.includes('502') || msg.includes('503') || msg.includes('504')) {
    return 'server';
  }
  return 'unknown';
}

/**
 * User-safe copy only — never raw stack traces or server dumps.
 * @param {unknown} err
 * @param {string} [fallback]
 */
export function toUserMessage(err, fallback = 'Something went wrong. Please try again.') {
  if (err && typeof err === 'object' && 'userFacing' in err && err.userFacing) {
    return String(/** @type {{ userFacing?: string }} */ (err).userFacing);
  }
  const kind = classifyError(err);
  switch (kind) {
    case 'timeout':
      return 'This is taking too long. Check your connection and try again.';
    case 'network':
      return 'We could not reach the server. Check your connection and try again.';
    case 'auth':
      return 'Your session expired. Please sign in again and retry.';
    case 'validation':
      return 'Some information could not be saved. Check your entries and try again.';
    case 'server':
      return 'The service is temporarily unavailable. Please try again in a moment.';
    default:
      return fallback;
  }
}

export function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Rejects if promise does not settle within ms.
 * @template T
 * @param {Promise<T>} promise
 * @param {number} ms
 * @param {string} [label]
 * @returns {Promise<T>}
 */
export function withTimeout(promise, ms, label = 'operation') {
  let timeoutId;
  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      const e = new Error(`${label} timed out after ${ms}ms`);
      e.name = 'TimeoutError';
      /** @type {{ code: string; isTimeout: boolean; label?: string }} */ (e).code = 'TIMEOUT';
      /** @type {{ code: string; isTimeout: boolean; label?: string }} */ (e).isTimeout = true;
      /** @type {{ code: string; isTimeout: boolean; label?: string }} */ (e).label = label;
      reject(e);
    }, ms);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => clearTimeout(timeoutId));
}

/**
 * One in-flight `auth.getSession` / `auth.refreshSession` per Supabase client.
 * Overlapping calls were stacking inside GoTrue and causing 15s getSession timeouts.
 * @type {WeakMap<import('@supabase/supabase-js').SupabaseClient, Promise<unknown>>}
 */
const coalescedGetSessionByClient = new WeakMap();
const coalescedRefreshSessionByClient = new WeakMap();

/** Cap proactive refresh: only call `refreshSession` when this close to expiry (seconds). */
const MAX_PROACTIVE_REFRESH_WINDOW_SEC = 45;
/** After a failed/timeout refresh, skip new refresh attempts this long (ms); return session if still valid. */
const AUTH_REFRESH_FAILURE_COOLDOWN_MS = 45_000;
/** @type {WeakMap<import('@supabase/supabase-js').SupabaseClient, number>} */
const lastAuthRefreshFailureAt = new WeakMap();

/**
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 */
function coalescedAuthGetSession(supabase) {
  let p = coalescedGetSessionByClient.get(supabase);
  if (!p) {
    p = withTimeout(supabase.auth.getSession(), 15000, 'supabase:getSession').finally(() => {
      coalescedGetSessionByClient.delete(supabase);
    });
    coalescedGetSessionByClient.set(supabase, p);
  }
  return p;
}

/**
 * Prefer this over raw `supabase.auth.getSession()` so hooks and writes share one in-flight call.
 * @param {import('@supabase/supabase-js').SupabaseClient | null} supabase
 */
export function getSupabaseAuthSessionCoalesced(supabase) {
  if (!supabase) {
    return Promise.resolve({ data: { session: null }, error: null });
  }
  return coalescedAuthGetSession(supabase);
}

/**
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 */
function coalescedAuthRefreshSession(supabase) {
  let p = coalescedRefreshSessionByClient.get(supabase);
  if (!p) {
    p = withTimeout(supabase.auth.refreshSession(), 15000, 'supabase:refreshSession').finally(() => {
      coalescedRefreshSessionByClient.delete(supabase);
    });
    coalescedRefreshSessionByClient.set(supabase, p);
  }
  return p;
}

/**
 * @param {unknown} err
 * @returns {boolean}
 */
export function isTransientFailure(err) {
  const k = classifyError(err);
  return k === 'timeout' || k === 'network' || k === 'server';
}

/**
 * @param {unknown} err
 * @returns {boolean}
 */
export function isAuthFailure(err) {
  return classifyError(err) === 'auth';
}

/**
 * Run async work with optional timeout, single retry on transient errors, and normalized result.
 * Always use for flows that toggle loading UI.
 *
 * @template T
 * @param {{
 *   name: string;
 *   fn: () => Promise<T>;
 *   timeoutMs?: number;
 *   retries?: number;
 *   retryDelayMs?: number;
 *   isRetryable?: (err: unknown) => boolean;
 *   onBeforeRetry?: () => Promise<void>;
 * }} options
 * @returns {Promise<{ ok: true; data: T; durationMs: number } | { ok: false; error: unknown; userMessage: string; durationMs: number }>}
 */
export async function runReliableAsync(options) {
  const {
    name,
    fn,
    timeoutMs = 60000,
    retries = 0,
    retryDelayMs = 600,
    isRetryable = isTransientFailure,
    onBeforeRetry,
  } = options;

  const started = performance.now();
  let lastErr;

  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt > 0) {
      devReliabilityLog('retry', { name, attempt, prev: classifyError(lastErr) });
      if (onBeforeRetry) await onBeforeRetry();
      await sleep(retryDelayMs);
    }
    try {
      const data = await withTimeout(fn(), timeoutMs, name);
      const durationMs = Math.round(performance.now() - started);
      devReliabilityLog('ok', { name, attempt, durationMs });
      devClickDebug(`runReliableAsync:ok name=${name}`, { attempt, durationMs });
      return { ok: true, data, durationMs };
    } catch (error) {
      lastErr = error;
      const durationMs = Math.round(performance.now() - started);
      devReliabilityLog('fail', {
        name,
        attempt,
        kind: classifyError(error),
        durationMs,
      });
      devClickDebug(`runReliableAsync:catch name=${name}`, {
        attempt,
        kind: classifyError(error),
        durationMs,
      });
      if (attempt < retries && isRetryable(error)) continue;
      return {
        ok: false,
        error,
        userMessage: toUserMessage(error),
        durationMs,
      };
    } finally {
      devClickDebug(`runReliableAsync:finally name=${name}`, { attempt });
    }
  }

  const durationMs = Math.round(performance.now() - started);
  return {
    ok: false,
    error: lastErr,
    userMessage: toUserMessage(lastErr),
    durationMs,
  };
}

/**
 * Supabase database mutation/query wrapper: runs `fn()` with timeout + normalized errors.
 * Does **not** call `getSession` / `getFreshSupabaseSession` — the Supabase client attaches
 * the session for PostgREST. Auth/RLS failures come back as real API errors from `fn()`.
 * For Bearer tokens to your own backend, use {@link getFreshAccessToken} / {@link fetchWithAuth}.
 *
 * @template T
 * @param {{
 *   name: string;
 *   supabaseClient: import('@supabase/supabase-js').SupabaseClient | null;
 *   fn: () => Promise<T>;
 *   timeoutMs?: number;
 * }} options
 * @returns {Promise<{ ok: true; data: T; durationMs: number } | { ok: false; error: unknown; userMessage: string; durationMs: number }>}
 */
export async function runSupabaseAuthedOperation(options) {
  const {
    name,
    supabaseClient,
    fn,
    timeoutMs = 55000,
  } = options;

  devClickDebug('runSupabaseAuthedOperation:start', { name, retriesDisabled: true, noGetSessionPreflight: true });

  return runReliableAsync({
    name,
    timeoutMs,
    retries: 0,
    retryDelayMs: 0,
    isRetryable: () => false,
    fn: async () => {
      devClickDebug(
        'runSupabaseAuthedOperation: executing Supabase fn without getSession preflight',
        { name, hasClient: !!supabaseClient }
      );
      try {
        const data = await fn();
        const summary =
          data === undefined
            ? 'undefined'
            : data !== null && typeof data === 'object' && 'error' in data && data.error
              ? `object with error: ${String(/** @type {{ error?: { message?: string } }} */ (data).error?.message || data.error)}`
              : typeof data;
        devClickDebug('runSupabaseAuthedOperation: Supabase fn completed', { name, resultSummary: summary });
        return data;
      } catch (err) {
        devClickDebug('runSupabaseAuthedOperation: Supabase fn catch', { name, err: String(err) });
        throw err;
      }
    },
  });
}

/**
 * Best-effort session refresh after sleep/wake — call before critical writes if needed.
 * @param {import('@supabase/supabase-js').SupabaseClient | null} supabase
 */
export async function refreshSupabaseSessionOnce(supabase) {
  if (!supabase) return false;
  try {
    const refreshed = await coalescedAuthRefreshSession(supabase);
    const error = refreshed?.error;
    if (error) {
      lastAuthRefreshFailureAt.set(supabase, Date.now());
      devReliabilityLog('refreshSession:error', { message: error.message });
      return false;
    }
    if (refreshed?.data?.session) {
      lastAuthRefreshFailureAt.delete(supabase);
    }
    return !!refreshed?.data?.session;
  } catch (e) {
    lastAuthRefreshFailureAt.set(supabase, Date.now());
    devReliabilityLog('refreshSession:catch', { message: String(e) });
    return false;
  }
}

/**
 * Get a valid Supabase session, refreshing when close to expiry.
 * @param {import('@supabase/supabase-js').SupabaseClient | null} supabase
 * @param {{ minTtlSeconds?: number; forceRefresh?: boolean }} [options]
 */
export async function getFreshSupabaseSession(supabase, options = {}) {
  if (!supabase) return null;
  const { minTtlSeconds = 60, forceRefresh = false } = options;
  devClickDebug('getFreshSupabaseSession:start', { minTtlSeconds, forceRefresh });
  try {
    const sessionRes = await getSupabaseAuthSessionCoalesced(supabase);
    let session = sessionRes?.data?.session || null;
    devClickDebug('getFreshSupabaseSession:after getSession', { hasSession: !!session });
    if (!session) {
      devClickDebug('getFreshSupabaseSession:early return — no session from getSession', {});
      return null;
    }

    const nowSec = Math.floor(Date.now() / 1000);
    const expiresAt = Number(session.expires_at || 0);
    const ttlSec = expiresAt ? expiresAt - nowSec : 0;
    const proactiveWindowSec = Math.min(minTtlSeconds, MAX_PROACTIVE_REFRESH_WINDOW_SEC);
    const needsRefresh =
      forceRefresh || !expiresAt || expiresAt <= nowSec + proactiveWindowSec;
    devClickDebug('getFreshSupabaseSession:refresh decision', {
      needsRefresh,
      forceRefresh,
      expiresAt,
      nowSec,
      ttlSec,
      proactiveWindowSec,
    });
    if (!needsRefresh) {
      devClickDebug('getFreshSupabaseSession: return session without refreshSession', { ttlSec });
      return session;
    }

    const lastFailAt = lastAuthRefreshFailureAt.get(supabase) ?? 0;
    const inFailureCooldown =
      !forceRefresh && Date.now() - lastFailAt < AUTH_REFRESH_FAILURE_COOLDOWN_MS;
    if (inFailureCooldown) {
      devClickDebug('getFreshSupabaseSession: skip refreshSession — recent failure cooldown', {
        ttlSec,
      });
      if (session.access_token && expiresAt > nowSec) return session;
      return null;
    }

    devClickDebug('getFreshSupabaseSession: calling refreshSession (coalesced)', {
      reason: forceRefresh ? 'forceRefresh' : 'nearExpiryOrNoExpiry',
    });
    let refreshed;
    try {
      refreshed = await coalescedAuthRefreshSession(supabase);
    } catch (e) {
      lastAuthRefreshFailureAt.set(supabase, Date.now());
      devReliabilityLog('getFreshSupabaseSession:refreshThrow', { message: String(e) });
      devClickDebug('getFreshSupabaseSession:refreshSession threw', { message: String(e) });
      if (session.access_token && expiresAt > nowSec) {
        devClickDebug('getFreshSupabaseSession: using pre-refresh session after throw', { ttlSec });
        return session;
      }
      return null;
    }
    if (refreshed?.error) {
      lastAuthRefreshFailureAt.set(supabase, Date.now());
      devReliabilityLog('getFreshSupabaseSession:refreshError', {
        message: refreshed.error.message,
      });
      devClickDebug('getFreshSupabaseSession:refreshSession error', { message: refreshed.error.message });
      if (session.access_token && expiresAt > nowSec) return session;
      return null;
    }
    session = refreshed?.data?.session || null;
    if (session?.access_token) {
      lastAuthRefreshFailureAt.delete(supabase);
    }
    devClickDebug('getFreshSupabaseSession:done after refresh', { hasSession: !!session });
    return session;
  } catch (e) {
    devReliabilityLog('getFreshSupabaseSession:catch', { message: String(e) });
    devClickDebug('getFreshSupabaseSession:catch', { message: String(e) });
    return null;
  }
}

/**
 * @param {import('@supabase/supabase-js').SupabaseClient | null} supabase
 * @param {{ minTtlSeconds?: number; forceRefresh?: boolean }} [options]
 */
export async function getFreshAccessToken(supabase, options = {}) {
  const { forceRefresh = false, minTtlSeconds = 90 } = options || {};
  /** Avoid piling `getSession()` when billing + dashboard effects run together (same stall class as invoice PATCH). */
  if (!forceRefresh) {
    const url = (process.env.REACT_APP_SUPABASE_URL || '').trim();
    const fromStorage = readValidSupabaseAccessTokenFromLocalStorage(url, minTtlSeconds);
    if (fromStorage) return fromStorage;
  }
  const session = await getFreshSupabaseSession(supabase, options);
  return session?.access_token || null;
}

/**
 * Express API base (billing, health, email, materials, etc.).
 *
 * Create React App replaces `process.env.REACT_APP_*` when you run `npm run build`
 * or `npm start`. Hosting (Firebase/Render) env vars do not change an already-built
 * `build/static/js/*.js` bundle — set `REACT_APP_DEVELLO_API_ORIGIN` at build time.
 *
 * Do not use `REACT_APP_PUBLIC_SITE_URL` here: the marketing/site origin is often not
 * the API host (e.g. Firebase + API on Render).
 */
function resolveDevelloApiOrigin() {
  // CRA/Webpack inlines `process.env.REACT_APP_*` and `NODE_ENV` at build time into string literals.
  // Do not gate reads on `typeof process !== "undefined"`: Webpack 5 often ships no browser `process`
  // polyfill, so that check is false in production and would drop a configured API URL to localhost.
  const nodeEnv = process.env.NODE_ENV;
  const fromEnv = process.env.REACT_APP_DEVELLO_API_ORIGIN;
  const trimmed = fromEnv && String(fromEnv).trim();
  if (trimmed) {
    return String(trimmed).replace(/\/$/, '');
  }

  if (nodeEnv === 'production') {
    const msg =
      '[Devello] Production build is missing REACT_APP_DEVELLO_API_ORIGIN. ' +
      'Set it to your API base (e.g. https://devello-backend.onrender.com) in the environment ' +
      'used for `npm run build`, then rebuild and redeploy the frontend.';
    devError(msg);
    throw new Error(msg);
  }

  return 'http://localhost:3001';
}

export const DEVELLO_API_ORIGIN = resolveDevelloApiOrigin();

/**
 * Normalize fetch failures (abort, HTTP, JSON parse).
 * @param {unknown} err
 * @param {number} [status]
 */
export function classifyHttpError(err, status) {
  if (status === 401 || status === 403) return 'auth';
  if (status != null && status >= 500) return 'server';
  if (status === 408 || status === 504) return 'timeout';
  return classifyError(err);
}

/**
 * Bounded `fetch` with AbortController, optional retry on transient failures,
 * safe JSON/text parsing, and dev-only logging. Use for all Render/backend calls.
 *
 * @param {string} url
 * @param {RequestInit & {
 *   timeoutMs?: number;
 *   retries?: number;
 *   parseJson?: boolean;
 *   name?: string;
 * }} [options]
 * @returns {Promise<{ ok: true; status: number; data: unknown; durationMs: number } | { ok: false; userMessage: string; error: unknown; status?: number }>}
 */
export async function fetchWithReliability(url, options = {}) {
  const {
    timeoutMs = 45000,
    retries = 1,
    parseJson = true,
    name = 'fetch',
    ...init
  } = options;

  const started = performance.now();
  let lastErr;
  let lastStatus;

  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt > 0) {
      devReliabilityLog('fetch:retry', { url: url.slice(0, 96), attempt });
      await sleep(700);
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
      devReliabilityLog('fetch:start', { name, attempt, url: url.slice(0, 120) });
      const res = await fetch(url, { ...init, signal: controller.signal });

      const text = await res.text();
      let data = text;
      if (parseJson && text) {
        try {
          data = JSON.parse(text);
        } catch {
          data = text;
        }
      }

      lastStatus = res.status;

      if (!res.ok) {
        const err = new Error(`HTTP ${res.status}`);
        /** @type {{ status?: number; body?: unknown }} */ (err).status = res.status;
        /** @type {{ status?: number; body?: unknown }} */ (err).body = data;
        throw err;
      }

      const durationMs = Math.round(performance.now() - started);
      devReliabilityLog('fetch:ok', { name, status: res.status, durationMs });
      return { ok: true, status: res.status, data, durationMs };
    } catch (error) {
      lastErr = error;
      const kind =
        /** @type {{ name?: string }} */ (error).name === 'AbortError'
          ? 'timeout'
          : classifyHttpError(error, lastStatus);
      devReliabilityLog('fetch:fail', {
        name,
        attempt,
        kind,
        message: String(/** @type {{ message?: string }} */ (error).message || error),
      });

      if (attempt < retries && (kind === 'timeout' || kind === 'network' || kind === 'server')) {
        // retry
      } else {
        return {
          ok: false,
          error,
          userMessage: toUserMessage(error),
          status: lastStatus,
        };
      }
    } finally {
      clearTimeout(timer);
    }
  }

  return {
    ok: false,
    error: lastErr,
    userMessage: toUserMessage(lastErr),
    status: lastStatus,
  };
}

/**
 * Auth-aware fetch wrapper for protected operations:
 * - gets fresh token
 * - retries once after forced auth refresh on 401/403
 * - preserves timeout/retry behavior from fetchWithReliability
 *
 * @param {string} url
 * @param {RequestInit & {
 *   timeoutMs?: number;
 *   retries?: number;
 *   parseJson?: boolean;
 *   name?: string;
 *   supabaseClient?: import('@supabase/supabase-js').SupabaseClient | null;
 *   requireAuth?: boolean;
 *   retryAuth?: boolean;
 * }} [options]
 */
export async function fetchWithAuth(url, options = {}) {
  const {
    supabaseClient = null,
    requireAuth = true,
    retryAuth = true,
    headers,
    ...rest
  } = options;

  const runAttempt = async (forceRefresh) => {
    const token = await getFreshAccessToken(supabaseClient, {
      minTtlSeconds: 90,
      forceRefresh,
    });
    if (requireAuth && !token) {
      return {
        ok: false,
        error: new Error('Missing auth session'),
        userMessage: 'Your session expired. Please sign in again and retry.',
        status: 401,
      };
    }
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
    return fetchWithReliability(url, {
      ...rest,
      headers: { ...(headers || {}), ...authHeaders },
    });
  };

  const first = await runAttempt(false);
  if (!first.ok && retryAuth && supabaseClient && (first.status === 401 || first.status === 403)) {
    devClickDebug('fetchWithAuth: retry with forceRefresh (no separate refreshSession call)', {});
    return runAttempt(true);
  }
  return first;
}

/**
 * Remove a detached DOM node safely (PDF/html2canvas off-screen containers).
 * @param {HTMLElement | null} el
 */
export function safeRemoveDetachedNode(el) {
  if (!el || !el.parentNode) return;
  try {
    el.parentNode.removeChild(el);
  } catch (_) {
    /* ignore */
  }
}
